<script setup>
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import {computed, nextTick, ref, watch} from "vue";
import Button from "primevue/button";
import Fieldset from "primevue/fieldset";
import ToggleSwitch from "primevue/toggleswitch";
import Panel from "primevue/panel";
import FeatureBlocker from "../../../components/ui-components/FeatureBlocker.vue";
import LangFlag from "vue-lang-code-flags";
import Quill from "quill";
import {getPrettyHTML} from "../../../../../helpers.js";
import { trans } from 'laravel-vue-i18n';

const emit = defineEmits(['done'])

const valid = defineModel('valid')
const model = defineModel()

const localeOptions = [
    { value: 'cs', label: trans('forms.language_cs') },
    { value: 'en', label: trans('forms.language_en') },
    { value: 'ko', label: trans('forms.language_ko') },
]

watch(() => model.value.settings.name, (now) => {
    valid.value = (now !== '')
})

const notifications = [
    {
        code: 'newClient',
        name: trans('reservations.notify_new_client'),
        description: trans('reservations.notify_new_client_desc'),
        feature: 'roori_notifications_new',
        version: 'Free',
        icon: 'pi-asterisk'
    },
    {
        code: 'confirmClient',
        name: trans('reservations.notify_confirm'),
        description: trans('reservations.notify_confirm_desc'),
        feature: 'roori_notifications_accept',
        version: 'Pro',
        icon: 'pi-thumbs-up'
    },
    {
        code: 'changeClient',
        name: trans('reservations.notify_change'),
        description: trans('reservations.notify_change_desc'),
        feature: 'roori_notifications_update',
        version: 'Pro',
        icon: 'pi-calendar'
    },
    {
        code: 'cancelClient',
        name: trans('reservations.notify_cancel'),
        description: trans('reservations.notify_cancel_desc'),
        feature: 'roori_notifications_cancel',
        version: 'Pro',
        icon: 'pi-calendar-times'
    },
    {
        code: 'reminderClient',
        name: trans('reservations.notify_reminder'),
        description: trans('reservations.notify_reminder_desc'),
        feature: 'roori_notifications_reminder',
        version: 'Pro',
        icon: 'pi-bell'
    },
 /* {
        code: 'retargetingClient',
        name: '',
        description: ''
    },*/
];

nextTick(() => {
    const quill = new Quill('#text1', {
        theme: 'snow',
        placeholder: trans('forms.setting_description'),
        modules: {
            toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{'color': []}, {'background': []}],
                [{'header': '1'}, {'header': '2'}, 'blockquote'],
                [{'align': []}],
                ['clean']
            ]
        }
    });
    model.value.settings.description = getPrettyHTML(quill.container.firstChild.innerHTML);
    quill.on('text-change', () => {
        model.value.settings.description_code = JSON.stringify(quill.getContents());
        model.value.settings.description = getPrettyHTML(quill.container.firstChild.innerHTML);
    });
})
</script>

<template>
    <div class="pb-6 pt-1 flex flex-col gap-4">
        <Panel :header="$t('forms.panel_basic_info')">
            <div class="flex max-md:flex-col mt-2 gap-4 mb-4">
                <div class="flex flex-col gap-1">
                    <label for="form-name" class="text-sm">{{ $t('forms.label_name') }}</label>
                    <InputText v-model="model.settings.name" id="form-name"></InputText>
                </div>
                <div class="flex flex-col gap-1 md:w-[220px]">
                    <label for="form-locale" class="text-sm">{{ $t('forms.label_language') }}</label>
                    <Select v-model="model.settings.language" id="form-name" :placeholder="$t('forms.setting_language_placeholder')" :options="localeOptions"
                            optionLabel="label" optionValue="value"></Select>
                </div>
            </div>
            <div id="text1" class="dark:border-neutral-700"></div>
        </Panel>
        <div class="flex max-md:flex-col gap-4">
            <Panel :header="$t('forms.panel_email_notifications')">
                <div class="flex flex-col gap-2">
                    <template v-for="notification in notifications">
                        <feature-blocker :name="notification.feature" :version="notification.version" inline>
                            <div class="flex gap-3 items-center">
                                <i :class="`pi ${notification.icon} ${model.settings.notifications[notification.code] ? 'text-gray-500' : 'text-gray-300'}`"></i>
                                <ToggleSwitch v-model="model.settings.notifications[notification.code]['email']" class="min-w-10"/>
                                <div class="flex flex-col">
                                    <p>{{notification.name}}</p>
                                    <small>{{notification.description}}</small>
                                </div>
                            </div>
                        </feature-blocker>
                    </template>
                </div>
            </Panel>
            <Panel :header="$t('forms.panel_sms_notifications')">
                <div class="text-sm -mt-3 mb-2 opacity-70 italic">{{ $t('forms.sms_credit_note') }}</div>
                <div class="flex flex-col gap-2">
                    <template v-for="notification in notifications">
                        <feature-blocker :name="notification.feature" :version="notification.version" inline>
                            <div class="flex gap-3 items-center">
                                <i :class="`pi ${notification.icon} ${model.settings.notifications[notification.code] ? 'text-gray-500' : 'text-gray-300'}`"></i>
                                <ToggleSwitch v-model="model.settings.notifications[notification.code]['sms']" class="min-w-10"/>
                                <div class="flex flex-col">
                                    <p>{{notification.name}}</p>
                                    <small>{{notification.description}}</small>
                                </div>
                            </div>
                        </feature-blocker>
                    </template>
                </div>
            </Panel>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
